// The flow's interstitials (no-photo gate, geo mismatch, note editor).
//
// Both Figma sections show these over a dimmed page rather than replacing it:
// mobile is a bottom sheet with a drag handle (frame 1:591), desktop is a
// centred card (frame 5:3291). Same component, one breakpoint.
//
// Three things about the behaviour are not obvious and were all bugs:
//
//  1. Callers pass an inline arrow for onDismiss, so its identity changes on
//     every render. With the focus effect keyed on it, every keystroke in the
//     note editor re-ran the effect and pulled focus off the textarea onto the
//     heading — you could type exactly one character. The handlers here are
//     registered ONCE and read the latest callback from a ref.
//  2. A click "outside" is a mousedown and a mouseup, and the browser reports
//     the click on the nearest common ancestor of the two. Dragging inside the
//     sheet — selecting text, moving a crop — and releasing over the scrim
//     therefore looked exactly like a click on the scrim and threw the sheet
//     away. Both halves must land on the scrim now, and a sheet holding typed
//     input opts out of scrim dismissal entirely.
//  3. It claims aria-modal, so Tab has to stay inside it.

import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { DESKTOP, DESKTOP_WIDE, DESKTOP_XL } from "./breakpoints";

interface Props {
  title: string;
  // Called on Escape and on a click on the scrim. Omit for a sheet the user
  // must answer.
  onDismiss?: () => void;
  // Set false for a sheet that holds work in progress — a typed note, a chosen
  // agency, a crop being adjusted. Escape and the sheet's own Cancel still
  // dismiss it; a stray click on the dimmed page does not, because the cost of
  // being wrong is losing what someone just did.
  dismissOnScrim?: boolean;
  labelledBy?: string;
  children?: React.ReactNode;
}

// Tab order inside the dialog. `:not([disabled])` matters: a disabled primary
// button is common here (nothing typed yet) and trapping focus onto it would
// leave Tab apparently doing nothing.
const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

// What a sheet opens focused on, when it has one. A sheet whose job is typing
// should arrive with the cursor already in the field; anything else opens on the
// heading, which is what a screen reader then announces.
const FIRST_FIELD = [
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
].join(",");

// Deliberately NOT the usual `offsetWidth || getClientRects().length` check:
// jsdom does no layout, so that reports every element as invisible and both the
// opening focus and the tab trap quietly stop working under test. Walking the
// computed styles asks the question directly and gives the same answer in both.
const isVisible = (el: HTMLElement): boolean => {
  let node: HTMLElement | null = el;
  while (node && node !== document.body) {
    if (node.hidden) return false;
    const style = window.getComputedStyle(node);
    if (style.display === "none" || style.visibility === "hidden") return false;
    node = node.parentElement;
  }
  return true;
};

const SubmissionSheet: React.FC<Props> = ({
  title,
  onDismiss,
  dismissOnScrim = true,
  children,
}) => {
  const sheetRef = useRef<HTMLDivElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const titleId = useRef(`sheet-${Math.random().toString(36).slice(2, 8)}`);
  // The latest callbacks, so the listeners below never need re-registering.
  const dismissRef = useRef(onDismiss);
  const scrimRef = useRef(dismissOnScrim);
  dismissRef.current = onDismiss;
  scrimRef.current = dismissOnScrim;
  // True only while a press that STARTED on the scrim is still held.
  const pressedScrim = useRef(false);
  const openerRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const active = document.activeElement as HTMLElement | null;
    openerRef.current = active;

    const sheet = sheetRef.current;
    let target: HTMLElement | null = null;
    if (sheet) {
      const field = sheet.querySelector(FIRST_FIELD) as HTMLElement | null;
      if (field && isVisible(field)) target = field;
    }
    if (!target) target = headingRef.current;
    if (target) target.focus();

    return () => {
      // Hand focus back to whatever opened the sheet. Without this it lands on
      // <body> and the next Tab starts over at the top of the page.
      const opener = openerRef.current;
      if (opener && typeof opener.focus === "function" && document.body.contains(opener)) {
        opener.focus();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" || e.key === "Esc") {
        if (dismissRef.current) dismissRef.current();
        return;
      }
      if (e.key !== "Tab") return;

      const sheet = sheetRef.current;
      if (!sheet) return;
      const all = sheet.querySelectorAll(FOCUSABLE);
      const items: HTMLElement[] = [];
      for (let i = 0; i < all.length; i += 1) {
        const el = all[i] as HTMLElement;
        if (isVisible(el)) items.push(el);
      }
      if (!items.length) {
        // Nothing to move to, so Tab must not walk out into the page behind.
        e.preventDefault();
        if (headingRef.current) headingRef.current.focus();
        return;
      }

      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      const inside = sheet.contains(active);
      if (e.shiftKey && (active === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent the page behind from scrolling while the sheet is up.
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);

  const onScrimMouseDown = (e: React.MouseEvent) => {
    pressedScrim.current = e.target === e.currentTarget;
  };

  const onScrimMouseUp = (e: React.MouseEvent) => {
    const pressed = pressedScrim.current;
    pressedScrim.current = false;
    if (!pressed || e.target !== e.currentTarget) return;
    if (!scrimRef.current || !dismissRef.current) return;
    dismissRef.current();
  };

  return (
    <Scrim
      onMouseDown={onScrimMouseDown}
      onMouseUp={onScrimMouseUp}
      data-dismissible={onDismiss && dismissOnScrim ? "true" : "false"}
    >
      <Sheet
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        /* styled-components v3 exposes the DOM node via innerRef, not ref. */
        innerRef={(el: HTMLDivElement) => {
          sheetRef.current = el;
        }}
      >
        <Handle aria-hidden="true" />
        <Title
          id={titleId.current}
          innerRef={(el: HTMLHeadingElement) => {
            headingRef.current = el;
          }}
          tabIndex={-1}
        >
          {title}
        </Title>
        {children}
      </Sheet>
    </Scrim>
  );
};

const Scrim = styled.div`
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(18, 38, 64, 0.45);
  z-index: 1200;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  @media all and (min-width: ${DESKTOP}) {
    align-items: center;
  }
`;

const Sheet = styled.div`
  background: #ffffff;
  width: 100%;
  max-width: 520px;
  border-radius: 16px 16px 0 0;
  padding: 10px 24px 28px;
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: 0 -8px 32px rgba(18, 38, 64, 0.18);
  text-align: center;

  /* The sheet owns the spacing between its children. Buttons carry no margins
     of their own — they are reused in the page body, where the layout there
     spaces them — so stacking two of them inside a plain block ran them
     together into what looked like one overlapping control. A flex column with
     a gap spaces whatever a sheet happens to contain, in any order. */
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 10px;

  /* Flexbox gap is missing on Safari below 14.1, and without it the buttons go
     right back to touching. Adjacent-sibling margins cover those browsers and
     nothing else, so the two spacing rules can never both apply. */
  @supports not (gap: 10px) {
    > * + * {
      margin-top: 10px;
    }
  }

  @media all and (min-width: ${DESKTOP}) {
    max-width: 396px;
    border-radius: 12px;
    padding: 30px 26px 26px;
    box-shadow: 0 18px 48px rgba(18, 38, 64, 0.28);
  }

  /* Tracks the content column so the modal does not look undersized next to it
     on a large screen — but more gently: a dialog that grows to the full column
     width stops reading as an interruption. */
  @media all and (min-width: ${DESKTOP_WIDE}) {
    max-width: 440px;
  }

  @media all and (min-width: ${DESKTOP_XL}) {
    max-width: 480px;
  }
`;

const Handle = styled.div`
  width: 64px;
  height: 4px;
  border-radius: 2px;
  background: #c9d3e0;
  /* Auto side margins still centre it as a flex item; the gap supplies the
     space underneath. flex: none keeps it 4px tall on a short viewport. */
  margin: 2px auto 6px;
  flex: none;

  @media all and (min-width: ${DESKTOP}) {
    display: none;
  }
`;

const Title = styled.h2`
  font-size: 22px;
  font-weight: 700;
  color: #122640;
  margin: 0;
  line-height: 1.25;
  outline: none;
`;

export default SubmissionSheet;
